import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import ProjectCard from '../components/ProjectCard';
import { useProjects } from '../contexts/ProjectsContext';
import { useTranslation } from '../contexts/LanguageContext';

const ProjectsPage: React.FC = () => {
  const { projects, loading } = useProjects();
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [activeFilter, setActiveFilter] = useState('all');
  const { t } = useTranslation();

  // Extract unique technologies from all projects
  const allTechnologies = [...new Set(projects.flatMap(project => project.technologies))];

  const filters = [
    { id: 'all', name: t('projects.filter.all') },
    { id: 'featured', name: t('projects.filter.featured') },
    ...allTechnologies.map(tech => ({ id: tech.toLowerCase(), name: tech })),
  ];

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else if (activeFilter === 'featured') {
      setFilteredProjects(projects.filter(project => project.featured));
    } else {
      setFilteredProjects(
        projects.filter(project =>
          project.technologies.some(tech => tech.toLowerCase() === activeFilter)
        )
      );
    }
  }, [activeFilter, projects]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <>
      <SEO
        title={t('projects.seo.title')}
        description={t('projects.seo.description')}
        keywords={['projects', 'portfolio', 'web development', 'case studies']}
        ogUrl="/projects"
      />

      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 mt-20"
        >
          <h1 className="text-4xl font-bold text-white mb-4 my-8">
            {t('projects.title')}
          </h1>
          <p className="text-lg text-primary-100 max-w-3xl">
            {t('projects.description')}
          </p>
        </motion.div>

        {/* Filter buttons */}
        {/* <div className="mb-10 overflow-x-auto pb-2">
          <div className="flex space-x-2">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeFilter === filter.id
                    ? 'bg-sky-600 text-white'
                    : 'bg-transparent text-white border-solid border-2 hover: '
                }`}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div> */}
        <div className="mb-12 overflow-x-auto py-4 premium-scrollbar group" style={{ position: 'relative' }}>
          {/* Premium Custom Scrollbar - Sky track with transparency, white thumb */}
          <style>{`
    .premium-scrollbar::-webkit-scrollbar {
      height: 6px; /* Ultra-thin for elegance */
    }
    .premium-scrollbar::-webkit-scrollbar-track {
      background: rgba(14, 165, 233, 0.15); /* sky-500/15 - subtle sky tint */
      border-radius: 9999px;
      margin: 0 24px; /* Generous inset for breathing room */
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1); /* Soft inner shadow for depth */
    }
    .premium-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(90deg, #ffffff, #f1f5f9); /* Pure white to slate-100 */
      border-radius: 9999px;
      border: 1.5px solid rgba(255, 255, 255, 0.3); /* Subtle white border */
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); /* Floating premium shadow */
    }
    .premium-scrollbar::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(90deg, #ffffff, #e2e8f0); /* Slightly warmer on hover */
      transform: scaleY(1.2); /* Gentle expand */
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
    /* Fade edges for cinematic overflow hint */
    .premium-scrollbar::before,
    .premium-scrollbar::after {
      content: '';
      position: absolute;
      top: 0;
      width: 32px;
      height: 100%;
      pointer-events: none;
      z-index: 10;
    }
  `}</style>

          <div className="flex items-center gap-5 px-6 min-w-max">
            {filters.map((filter, index) => (
              <button
                key={filter.id}
                onClick={() => {
                  // Small navigation delay (150ms) for premium "thoughtful" feel
                  const timer = setTimeout(() => setActiveFilter(filter.id), 150);
                  return () => clearTimeout(timer);
                }}
                className={`
          relative px-8 py-3.5 rounded-full text-sm font-semibold tracking-widest uppercase
          transition-all duration-500 ease-out overflow-hidden isolate
          group/button
          ${activeFilter === filter.id
                    ? 'text-white shadow-2xl shadow-sky-500/40'
                    : 'text-white/70 hover:text-white/90'
                  }
          /* Staggered entrance animation */
          animate-in fade-in slide-in-from-left-2 duration-700
        `}
                style={{ animationDelay: `${index * 75}ms` }} /* Stagger filters on load */
              >
                {/* Active: Multi-layer glassmorphism with sky gradient */}
                {activeFilter === filter.id && (
                  <>
                    {/* Outer glow ring */}
                    <span className="absolute -inset-1 rounded-full bg-sky-500/30 blur-xl animate-pulse-slow" />
                    {/* Main gradient fill */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-400 via-sky-500 to-sky-600 opacity-95" />
                    {/* Inner frosted overlay */}
                    <span className="absolute inset-px rounded-full bg-white/10 backdrop-blur-xs" />
                    {/* Floating highlight streak */}
                    <span className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-white/20 to-transparent skew-x-12 -translate-x-full group-hover/button:translate-x-full transition-transform duration-1000" />
                  </>
                )}

                {/* Inactive: Minimal hover lift */}
                {activeFilter !== filter.id && (
                  <span className="absolute inset-0 rounded-full bg-white/5 group-hover/button:bg-white/8 scale-95 group-hover/button:scale-100 transition-all duration-500" />
                )}

                {/* Universal micro-shine */}
                <span className="absolute top-1 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-white/40 rounded-full blur-md opacity-0 group-hover/button:opacity-100 transition-opacity duration-700" />

                {/* Text */}
                <span className="relative z-10 mix-blend-plus-lighter">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-600"></div>
          </div>
        ) : filteredProjects.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <h3 className="text-xl font-semibold text-primary-800 mb-2">
              {t('projects.empty.title')}
            </h3>
            <p className="text-primary-600">
              {t('projects.empty.text')}
            </p>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default ProjectsPage;